/**
 * Redux middleware for WebSocket integration
 * Automatically syncs WebSocket events with Redux store
 */

import { Middleware } from '@reduxjs/toolkit';
import { getWebSocketService } from '../../services/websocketService';
import {
  setConnectionStatus,
  addSubscribedTopic,
  removeSubscribedTopic,
  receiveTrade,
  receivePosition,
  receiveBreach,
  receiveOrder,
  receiveAlert,
  clearAllData
} from '../slices/websocketSlice';

// Action types for WebSocket control
export const WEBSOCKET_CONNECT = 'websocket/connect';
export const WEBSOCKET_DISCONNECT = 'websocket/disconnect';
export const WEBSOCKET_SUBSCRIBE = 'websocket/subscribe';
export const WEBSOCKET_UNSUBSCRIBE = 'websocket/unsubscribe';

// Action creators
export const wsConnect = (userId: string, token?: string) => ({
  type: WEBSOCKET_CONNECT,
  payload: { userId, token }
});

export const wsDisconnect = () => ({
  type: WEBSOCKET_DISCONNECT
});

export const wsSubscribe = (topic: string, token?: string) => ({
  type: WEBSOCKET_SUBSCRIBE,
  payload: { topic, token }
});

export const wsUnsubscribe = (topic: string) => ({
  type: WEBSOCKET_UNSUBSCRIBE,
  payload: { topic }
});

/**
 * WebSocket Redux middleware
 */
export const websocketMiddleware: Middleware = (store) => {
  const wsService = getWebSocketService();
  const subscriptions = new Map<string, () => void>();
  let statusListenerSetup = false;

  return (next) => (action) => {
    // Set up status listener on first action (not during initialization)
    if (!statusListenerSetup) {
      statusListenerSetup = true;
      wsService.onStatusChange((status) => {
        // Use setTimeout to avoid dispatching during dispatch
        setTimeout(() => {
          store.dispatch(setConnectionStatus(status));
        }, 0);
      });
    }
    switch (action.type) {
      case WEBSOCKET_CONNECT: {
        const { userId, token } = action.payload;
        wsService.connect(userId, token);

        // Set up topic subscriptions with Redux dispatch
        const topics = ['trades', 'positions', 'breaches', 'orders', 'alerts'] as const;

        topics.forEach(topic => {
          // Clean up existing subscription if any
          const existingUnsubscribe = subscriptions.get(topic);
          if (existingUnsubscribe) {
            existingUnsubscribe();
          }

          // Create new subscription
          const unsubscribe = wsService.subscribe(
            topic,
            (data) => {
              // Dispatch appropriate action based on topic
              switch (topic) {
                case 'trades':
                  store.dispatch(receiveTrade(data));
                  break;
                case 'positions':
                  store.dispatch(receivePosition(data));
                  break;
                case 'breaches':
                  store.dispatch(receiveBreach(data));
                  break;
                case 'orders':
                  store.dispatch(receiveOrder(data));
                  break;
                case 'alerts':
                  store.dispatch(receiveAlert(data));
                  break;
              }
            },
            token
          );

          subscriptions.set(topic, unsubscribe);
          store.dispatch(addSubscribedTopic(topic));
        });
        break;
      }

      case WEBSOCKET_DISCONNECT: {
        // Clean up all subscriptions
        subscriptions.forEach((unsubscribe, topic) => {
          unsubscribe();
          store.dispatch(removeSubscribedTopic(topic));
        });
        subscriptions.clear();

        // Clear data and disconnect
        store.dispatch(clearAllData());
        wsService.disconnect();
        break;
      }

      case WEBSOCKET_SUBSCRIBE: {
        const { topic, token } = action.payload;

        // Clean up existing subscription if any
        const existingUnsubscribe = subscriptions.get(topic);
        if (existingUnsubscribe) {
          existingUnsubscribe();
        }

        // Create new subscription
        const unsubscribe = wsService.subscribe(
          topic,
          (data) => {
            // Dispatch appropriate action based on topic
            switch (topic) {
              case 'trades':
                store.dispatch(receiveTrade(data));
                break;
              case 'positions':
                store.dispatch(receivePosition(data));
                break;
              case 'breaches':
                store.dispatch(receiveBreach(data));
                break;
              case 'orders':
                store.dispatch(receiveOrder(data));
                break;
              case 'alerts':
                store.dispatch(receiveAlert(data));
                break;
            }
          },
          token
        );

        subscriptions.set(topic, unsubscribe);
        store.dispatch(addSubscribedTopic(topic));
        break;
      }

      case WEBSOCKET_UNSUBSCRIBE: {
        const { topic } = action.payload;
        const unsubscribe = subscriptions.get(topic);
        if (unsubscribe) {
          unsubscribe();
          subscriptions.delete(topic);
          store.dispatch(removeSubscribedTopic(topic));
        }
        break;
      }
    }

    return next(action);
  };
};